import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { z } from "zod";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import { usd } from "@/lib/pricing";
import { optimizeImg, srcSet } from "@/lib/img";

const fleetQuery = queryOptions({
  queryKey: ["cars", "all"],
  queryFn: async () => {
    const { data, error } = await supabase
      .from("cars").select("*").eq("active", true)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data ?? [];
  },
});

const unavailableQuery = queryOptions({
  queryKey: ["unavailable-ranges"],
  queryFn: async () => {
    const { data, error } = await supabase.rpc("get_unavailable_ranges");
    if (error) throw error;
    return (data ?? []) as { car_id: string; start_date: string; end_date: string }[];
  },
});

export function rangesOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string) {
  return aStart <= bEnd && bStart <= aEnd;
}

const searchSchema = z.object({
  start: fallback(z.string().optional(), undefined),
  end: fallback(z.string().optional(), undefined),
});

export const Route = createFileRoute("/fleet")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    meta: [
      { title: "Luxury Car Rental Miami Fleet | Exotic & SUV Rentals | Creed" },
      { name: "description", content: "Rent luxury cars in Miami from Creed's premium fleet. Exotics, SUVs, convertibles & sedans delivered to MIA, South Beach & Fort Lauderdale. Insurance included." },
      { property: "og:title", content: "Luxury Car Rental Miami Fleet | Exotic & SUV Rentals | Creed" },
      { property: "og:description", content: "Premium exotics, SUVs, convertibles & sedans delivered across Miami-Dade. Insurance always included." },
      { property: "og:url", content: "https://creedcarental.com/fleet" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://creedcarental.com/fleet" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Creed Car Rental Fleet",
          url: "https://creedcarental.com/fleet",
          about: "Luxury car rentals in Miami — exotics, SUVs, convertibles, and sedans.",
        }),
      },
    ],
  }),
  loader: ({ context }) => Promise.all([
    context.queryClient.ensureQueryData(fleetQuery),
    context.queryClient.ensureQueryData(unavailableQuery),
  ]),
  component: Fleet,
  errorComponent: () => <div className="p-10 text-center">Failed to load fleet.</div>,
  notFoundComponent: () => <div className="p-10 text-center">Not found.</div>,
});

type Category = "all" | "exotic" | "suv" | "convertible" | "sedan";
type SortKey = "featured" | "newest" | "price_asc" | "price_desc";
type PriceBand = "all" | "under100" | "100to250" | "over250";

const EXOTIC = ["lamborghini", "ferrari", "mclaren", "porsche", "aston", "bentley", "rolls", "maserati"];
const SUV_HINTS = ["suv", "cayenne", "urus", "wagon", "rover", "escalade", "g-wagon", "g class", "g-class", "x5", "x7", "gle", "gls"];
const CONV_HINTS = ["convertible", "cabriolet", "spider", "roadster", "spyder"];
const SEDAN_HINTS = ["sedan", "s class", "s-class", "7 series", "a8", "ghost", "phantom", "panamera"];

function classify(car: any): Category {
  if (car.body_type) {
    const bt = String(car.body_type).toLowerCase();
    if (bt === "convertible") return "convertible";
    if (bt === "suv" || bt === "truck" || bt === "van") return "suv";
    if (bt === "exotic") return "exotic";
    if (bt === "sedan" || bt === "coupe") return "sedan";
  }
  const hay = `${car.make ?? ""} ${car.model ?? ""} ${car.trim ?? ""}`.toLowerCase();
  if (CONV_HINTS.some(h => hay.includes(h))) return "convertible";
  if (SUV_HINTS.some(h => hay.includes(h))) return "suv";
  if (EXOTIC.some(h => hay.includes(h))) return "exotic";
  if (SEDAN_HINTS.some(h => hay.includes(h))) return "sedan";
  return "sedan";
}

function bodyLabel(car: any): string | null {
  const bt = car.body_type ? String(car.body_type).toLowerCase() : null;
  if (bt) {
    const map: Record<string, string> = {
      exotic: "Exotic", suv: "SUV", convertible: "Convertible",
      sedan: "Sedan", coupe: "Coupe", truck: "Truck", van: "Van",
    };
    if (map[bt]) return map[bt];
  }
  const c = classify(car);
  return c === "all" ? null : c.charAt(0).toUpperCase() + c.slice(1);
}

function Fleet() {
  const { data: cars } = useSuspenseQuery(fleetQuery);
  const { data: unavailable } = useSuspenseQuery(unavailableQuery);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/fleet" });
  const [cat, setCat] = useState<Category>("all");
  const [sort, setSort] = useState<SortKey>("featured");
  const [price, setPrice] = useState<PriceBand>("all");
  const startDate = search.start ?? "";
  const endDate = search.end ?? "";
  const setStartDate = (v: string) =>
    navigate({ search: (prev: { start?: string; end?: string }) => ({ ...prev, start: v || undefined }), replace: true });
  const setEndDate = (v: string) =>
    navigate({ search: (prev: { start?: string; end?: string }) => ({ ...prev, end: v || undefined }), replace: true });

  const datesActive = startDate && endDate && startDate <= endDate;
  const unavailableCarIds = useMemo(() => {
    if (!datesActive) return new Set<string>();
    const ids = new Set<string>();
    for (const b of unavailable) {
      if (rangesOverlap(startDate, endDate, b.start_date, b.end_date)) ids.add(b.car_id);
    }
    return ids;
  }, [unavailable, startDate, endDate, datesActive]);

  const inBand = (rate: number, band: PriceBand) => {
    if (band === "all") return true;
    if (band === "under100") return rate < 100;
    if (band === "100to250") return rate >= 100 && rate <= 250;
    return rate > 250;
  };

  const filtered = useMemo(() => {
    let list = cat === "all" ? cars : cars.filter(c => classify(c) === cat);
    list = list.filter(c => inBand(Number(c.daily_rate) || 0, price));
    if (sort === "price_asc") list = [...list].sort((a, b) => Number(a.daily_rate) - Number(b.daily_rate));
    else if (sort === "price_desc") list = [...list].sort((a, b) => Number(b.daily_rate) - Number(a.daily_rate));
    else if (sort === "newest") list = [...list].sort((a, b) =>
      new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime()
    );
    else {
      // featured: featured first, then newest
      list = [...list].sort((a, b) => {
        const fa = a.is_featured ? 1 : 0;
        const fb = b.is_featured ? 1 : 0;
        if (fa !== fb) return fb - fa;
        return new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime();
      });
    }
    return list;
  }, [cars, cat, sort, price]);

  const minRate = useMemo(
    () => cars.reduce((m, c) => Math.min(m, Number(c.daily_rate) || Infinity), Infinity),
    [cars]
  );

  // Hide spec chips when every car shares the same value (no signal)
  const uniform = useMemo(() => {
    const allSame = (key: string) => {
      const vals = cars.map(c => (c as any)[key]).filter(v => v != null && v !== "");
      return vals.length === cars.length && new Set(vals.map(String)).size <= 1;
    };
    return { seats: allSame("seats"), transmission: allSame("transmission"), fuel_type: allSame("fuel_type") };
  }, [cars]);

  const activeCount = filtered.length;
  const availableCount = datesActive ? filtered.filter(c => !unavailableCarIds.has(c.id)).length : activeCount;
  const showFilteredCount = cat !== "all" || price !== "all" || datesActive;

  const cats: { key: Category; label: string }[] = [
    { key: "all", label: "All" },
    { key: "exotic", label: "Exotic" },
    { key: "suv", label: "SUV" },
    { key: "convertible", label: "Convertible" },
    { key: "sedan", label: "Sedan" },
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />

      {/* Hero header */}
      <section className="relative isolate overflow-hidden border-b border-border/60 bg-espresso py-20 md:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 80% at 85% 20%, oklch(0.72 0.15 65 / 0.35), transparent 60%), radial-gradient(50% 70% at 10% 90%, oklch(0.72 0.15 65 / 0.18), transparent 60%)",
          }}
          aria-hidden
        />
        <svg
          className="pointer-events-none absolute -right-24 -top-24 h-[520px] w-[520px] text-accent/20"
          viewBox="0 0 600 600"
          fill="none"
          aria-hidden
        >
          <circle cx="300" cy="300" r="220" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="160" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="100" stroke="currentColor" strokeWidth="1" />
        </svg>
        <div className="relative mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-accent">Our fleet</div>
          <h1 className="mt-4 font-display text-5xl text-cream md:text-6xl">Find your drive</h1>
          <p className="mt-4 max-w-xl text-cream/70">
            Daily rates with weekly &amp; monthly discounts. Insurance always included. Delivery across Miami-Dade.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {Number.isFinite(minRate) && (
              <div className="inline-flex items-baseline gap-2 rounded-full border border-accent/30 bg-cream/5 px-4 py-2">
                <span className="text-xs uppercase tracking-[0.2em] text-cream/60">Starting at</span>
                <span className="font-display text-xl text-accent">{usd(minRate)}</span>
                <span className="text-xs text-cream/60">/ day</span>
              </div>
            )}
            <div className="inline-flex items-center rounded-full border border-cream/15 bg-cream/5 px-4 py-2 text-xs text-cream/70">
              {datesActive
                ? `${availableCount} available for your dates`
                : showFilteredCount
                  ? `${activeCount} of ${cars.length} match`
                  : `${cars.length} ${cars.length === 1 ? "vehicle" : "vehicles"} available`}
            </div>
          </div>
        </div>
      </section>

      {/* Trust / policy strip */}
      <section aria-labelledby="fleet-policies" className="border-b border-border/60 bg-card/60">
        <h2 id="fleet-policies" className="sr-only">Our rental policies</h2>
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-6 gap-y-2 px-6 py-3 text-xs text-espresso/70 sm:text-sm">
          <span>Drivers 25+ with valid license</span>
          <span className="hidden h-1 w-1 rounded-full bg-espresso/30 sm:block" />
          <span>Insurance included</span>
          <span className="hidden h-1 w-1 rounded-full bg-espresso/30 sm:block" />
          <span>Refundable deposit, released within 48 hrs</span>
          <span className="hidden h-1 w-1 rounded-full bg-espresso/30 sm:block" />
          <span>Free Miami-Dade delivery</span>
        </div>
      </section>

      {/* Filters */}
      <section aria-labelledby="fleet-filters" className="mx-auto max-w-7xl px-6 py-8">
        <h2 id="fleet-filters" className="sr-only">Filter your search</h2>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-wrap gap-2">
              {cats.map(c => (
                <button
                  key={c.key}
                  onClick={() => setCat(c.key)}
                  className={`rounded-full border px-4 py-1.5 text-sm transition ${
                    cat === c.key
                      ? "border-accent bg-accent text-espresso"
                      : "border-border bg-card text-espresso/70 hover:border-accent hover:text-espresso"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-end gap-3">
              <select
                value={sort}
                onChange={e => setSort(e.target.value as SortKey)}
                className="shrink-0 rounded-full border border-border bg-card px-4 py-1.5 text-sm text-espresso shadow-sm transition hover:border-accent focus:border-accent focus:outline-none"
                aria-label="Sort fleet"
              >
                <option value="featured">Featured</option>
                <option value="newest">Newest</option>
                <option value="price_asc">Price: low to high</option>
                <option value="price_desc">Price: high to low</option>
              </select>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-[0.2em] text-espresso/50">Price</span>
            {([
              { k: "all", label: "Any" },
              { k: "under100", label: "Under $100" },
              { k: "100to250", label: "$100–250" },
              { k: "over250", label: "$250+" },
            ] as { k: PriceBand; label: string }[]).map(p => (
              <button
                key={p.k}
                onClick={() => setPrice(p.k)}
                className={`rounded-full border px-3 py-1 text-xs transition ${
                  price === p.k
                    ? "border-accent bg-accent text-espresso"
                    : "border-border bg-card text-espresso/70 hover:border-accent hover:text-espresso"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <span className="text-xs uppercase tracking-[0.2em] text-espresso/50 self-center">Dates</span>
            <label className="flex flex-col text-[11px] uppercase tracking-wide text-espresso/50">
              Pickup
              <input
                type="date"
                value={startDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={e => setStartDate(e.target.value)}
                className="mt-1 rounded-full border border-border bg-card px-3 py-1 text-xs text-espresso focus:border-accent focus:outline-none"
              />
            </label>
            <label className="flex flex-col text-[11px] uppercase tracking-wide text-espresso/50">
              Return
              <input
                type="date"
                value={endDate}
                min={startDate || new Date().toISOString().split("T")[0]}
                onChange={e => setEndDate(e.target.value)}
                className="mt-1 rounded-full border border-border bg-card px-3 py-1 text-xs text-espresso focus:border-accent focus:outline-none"
              />
            </label>
            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(""); setEndDate(""); }}
                className="self-center rounded-full border border-border bg-card px-3 py-1 text-xs text-espresso/70 hover:border-accent"
              >
                Clear dates
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section aria-labelledby="fleet-grid" className="mx-auto max-w-7xl px-6 pb-16">
        <h2 id="fleet-grid" className="sr-only">Available luxury vehicles</h2>
        {filtered.length === 0 ? (
          <div className="rounded-tr-3xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground">
            No vehicles match this filter. Try another category.
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map(c => {
              const body = bodyLabel(c);
              const topFeature = Array.isArray(c.features) && c.features.length > 0 ? c.features[0] : null;
              const subtitle = topFeature || (body ? `${body} · Delivered in Miami-Dade` : "Delivered in Miami-Dade");
              const showSeats = c.seats && !uniform.seats;
              const showTrans = c.transmission && !uniform.transmission;
              const showFuel = c.fuel_type && !uniform.fuel_type;
              const unavailableForDates = datesActive && unavailableCarIds.has(c.id);
              return (
                <Link
                  key={c.id}
                  to="/cars/$carId"
                  params={{ carId: c.id }}
                  search={datesActive ? { start: startDate, end: endDate } : {}}
                  className={`group overflow-hidden rounded-tr-3xl bg-card shadow-sm transition hover:shadow-md ${unavailableForDates ? "opacity-60" : ""}`}
                >
                  <div className="relative aspect-[4/3] w-full overflow-clip bg-muted">
                    {c.photos?.[0] ? (
                      <img
                        src={optimizeImg(c.photos[0], { width: 800 })}
                        srcSet={srcSet(c.photos[0], [400, 600, 900])}
                        sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                        alt={`${c.make} ${c.model}`}
                        width={800}
                        height={600}
                        loading="lazy"
                        decoding="async"
                        className={`h-full w-full object-cover transition duration-500 group-hover:scale-105 ${unavailableForDates ? "grayscale" : ""}`}
                      />
                    ) : (
                      <div className="grid h-full place-items-center text-sm text-muted-foreground">No image</div>
                    )}
                    {body && (
                      <span className="absolute left-3 top-3 rounded-full bg-espresso/80 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-cream backdrop-blur">
                        {body}
                      </span>
                    )}
                    {unavailableForDates ? (
                      <span className="absolute right-3 top-3 rounded-full bg-espresso px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-cream shadow">
                        Booked
                      </span>
                    ) : c.is_featured && (
                      <span className="absolute right-3 top-3 rounded-full bg-accent px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-espresso shadow">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="font-display text-xl text-espresso">
                      {c.year} {c.make} {c.model}
                      {c.trim && <span className="text-espresso/60"> {c.trim}</span>}
                    </div>
                    <div className="mt-1 line-clamp-1 text-sm text-muted-foreground">
                      {subtitle}
                    </div>

                    {(showSeats || showTrans || showFuel) && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {showSeats && (
                          <span className="rounded-full bg-secondary/60 px-2.5 py-0.5 text-xs text-espresso/70">
                            {c.seats} seats
                          </span>
                        )}
                        {showTrans && (
                          <span className="rounded-full bg-secondary/60 px-2.5 py-0.5 text-xs text-espresso/70 capitalize">
                            {c.transmission}
                          </span>
                        )}
                        {showFuel && (
                          <span className="rounded-full bg-secondary/60 px-2.5 py-0.5 text-xs text-espresso/70 capitalize">
                            {c.fuel_type}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="mt-4 flex items-baseline justify-between border-t border-border/60 pt-4">
                      <div>
                        <span className="font-display text-2xl text-accent">{usd(Number(c.daily_rate))}</span>
                        <span className="text-sm text-muted-foreground"> / day</span>
                      </div>
                      {Number(c.weekly_discount_pct) > 0 && (
                        <span className="text-xs text-accent">Save {c.weekly_discount_pct}% weekly</span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Closing CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-20">
        <div className="relative isolate overflow-hidden rounded-tr-3xl bg-espresso px-8 py-12 md:px-12 md:py-16">
          <div
            className="pointer-events-none absolute inset-0 opacity-70"
            style={{
              background:
                "radial-gradient(60% 80% at 90% 10%, oklch(0.72 0.15 65 / 0.30), transparent 60%)",
            }}
            aria-hidden
          />
          <div className="relative grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.3em] text-accent">Concierge sourcing</div>
              <h2 className="mt-3 font-display text-3xl text-cream md:text-4xl">
                Looking for a specific car?
              </h2>
              <p className="mt-3 max-w-xl text-cream/70">
                We can source it. Tell us the make, model, and dates — we'll get back to you within the hour.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex shrink-0 items-center justify-center rounded-full bg-accent px-6 py-3 font-medium text-espresso transition hover:opacity-90"
            >
              Request a car
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
